import type { SectorVariants } from "$root/functional-areas/outlook/repository";
import { useMemo } from "react";

export const MAP_ASSET_CLASS: Record<string, "EQUITY" | "FIXED_INCOME" | "COMMODITIES" | "FIXEDINCOME"> = {
	FI: "FIXEDINCOME",
	CO: "COMMODITIES",
	EQ: "EQUITY",
};

export type OutlookGeography =
	| "EM"
	| "JP"
	| "US"
	| "EU"
	| "GLOBAL"
	| "UK"
	| "EU_SMALL_MID_CAP"
	| "PACIFIC_EX_JP"
	| "US_SMALL_CAP"
	| "US_MID_CAP";

export const MAP_GEOGRAPHY: Record<string, OutlookGeography> = {
	MARKETS: "GLOBAL",
	EU: "EU",
	US: "US",
	JP: "JP",
	EM: "EM",
	UK: "UK",
	EU_SMALL_MID_CAP: "EU_SMALL_MID_CAP",
	PACIFIC_EX_JP: "PACIFIC_EX_JP",
	US_SMALL_CAP: "US_SMALL_CAP",
	US_MID_CAP: "US_MID_CAP",
	"": "GLOBAL",
};

export const MAP_SECTOR: Record<string, SectorVariants> = {
	ALL_SECTORS: "ALL",
	GLOBAL: "GLOBAL",
	PRECIOUS_METALS: "PRECIOUS_METALS",
	ENERGY: "ENERGY",
	FINANCIALS: "FINANCIALS",
	INDUSTRIALS: "INDUSTRIALS",
	CONSUMER_STAPLES: "CONSUMER_STAPLES",
	COMMUNICATION_SERVICES: "COMMUNICATION_SERVICES",
	INFORMATION_TECHNOLOGY: "INFORMATION_TECHNOLOGY",
	CONSUMER_DISCRETIONARY: "CONSUMER_DISCRETIONARY",
	HEALTHCARE: "HEALTHCARE",
	HEALTH_CARE: "HEALTHCARE",
	MATERIALS: "MATERIALS",
	UTILITIES: "UTILITIES",
	REAL_ESTATE: "REAL_ESTATE",
	AGRICULTURE: "AGRICULTURE",
	INDUSTRIAL_METALS: "INDUSTRIAL_METALS",
	LONG_GOV: "LONG_GOV",
	MEDIUM_GOV: "MEDIUM_GOV",
	SHORT_GOV: "SHORT_GOV",
	CORP: "CORP",
	GOV: "GOV",
	HY: "HY",
	ALL: "ALL",
};

const useOutlookComposition = (
	firstFilter: string,
	secondFilter: string,
	thirdFilter: string,
): {
	assetClass: "EQUITY" | "COMMODITIES" | "FIXED_INCOME" | "FIXEDINCOME";
	geography: OutlookGeography;
	sector: SectorVariants;
} => {
	const { assetClass, geography, sector } = useMemo(
		() => getOutlookComposition(firstFilter, secondFilter, thirdFilter),
		[firstFilter, secondFilter, thirdFilter],
	);

	return { assetClass, geography, sector };
};

export default useOutlookComposition;

export function getOutlookComposition(
	firstFilter: string,
	secondFilter: string,
	thirdFilter: string,
): {
	assetClass: "EQUITY" | "COMMODITIES" | "FIXED_INCOME" | "FIXEDINCOME";
	geography: OutlookGeography;
	sector: SectorVariants;
} {
	const clearSectors = (ac: string, s?: string) => {
		const defaultConf: Record<string, SectorVariants> = {
			commodities: "PRECIOUS_METALS",
			fixedIncome: "ALL",
			equity: "ALL",
		};
		if (!s) {
			return defaultConf[ac];
		}
		return MAP_SECTOR[s] ?? "ALL";
	};

	const sanitaizedAssetClass = firstFilter ? MAP_ASSET_CLASS[firstFilter] : "EQUITY";
	const sanitaizedGeoFilter = secondFilter && MAP_GEOGRAPHY[secondFilter] ? MAP_GEOGRAPHY[secondFilter] : "GLOBAL";
	const sanitaizedSectorFilter =
		thirdFilter && firstFilter !== "CO"
			? clearSectors(sanitaizedAssetClass, thirdFilter)
			: clearSectors(sanitaizedAssetClass, secondFilter);

	return {
		assetClass: sanitaizedAssetClass,
		geography: sanitaizedGeoFilter,
		sector: sanitaizedSectorFilter,
	};
}
